/**
 * Layout do participe (e telas públicas):
 *   - Mobile (xs/sm): hero azul compacto no topo + card branco abaixo (empilhado).
 *   - Desktop (md+): fundo split em duas zonas FIXAS (esquerda azul gradient + direita
 *     creme claro). A logo fica posicionada no topo do painel azul (lugar estratégico).
 *     O card branco do formulário fica CENTRALIZADO horizontalmente, atravessando a
 *     linha de divisão — metade sobre o azul, metade sobre o claro — e a página
 *     rola normalmente se o conteúdo for longo (background+logo continuam fixos).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, LinearProgress, Typography } from '@mui/material';

function SplitView(props) {
  const { loading, contextImage, children } = props;
  const { alt } = contextImage;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        // Mobile: bg claro plano (o hero azul é renderizado como elemento separado abaixo).
        // Desktop: bg fica transparente — usamos divs fixos com gradient azul + creme.
        background: { xs: '#f4f9fb', md: 'transparent' },
      }}
    >
      {loading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            height: 3,
          }}
        />
      )}

      {/* BACKGROUND FIXO (desktop): split azul (esquerda) + creme (direita).
          `position: fixed` faz o fundo permanecer ancorado na viewport mesmo
          quando a página rola — só o card branco se move com o scroll. */}
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: { md: '48%', lg: '50%' },
            height: '100%',
            background: `
              radial-gradient(ellipse at 20% 18%, rgba(9, 175, 223, 0.55), transparent 60%),
              radial-gradient(ellipse at 82% 85%, rgba(0, 74, 97, 0.45), transparent 55%),
              linear-gradient(135deg, #0080ad 0%, #197997 55%, #004A61 100%)
            `,
          }}
        />
        <Box sx={{ flex: 1, height: '100%', background: '#f4f9fb' }} />
      </Box>

      {/* LOGO FIXA (desktop): canto superior esquerdo do painel azul. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          top: { md: 32, lg: 40, xl: 48 },
          left: { md: 40, lg: 56, xl: 72 },
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Box
          component="img"
          src="/img/logo_branca.png"
          alt={alt || 'Sua Formatura'}
          sx={{
            width: { md: 180, lg: 220, xl: 240 },
            height: 'auto',
            filter: 'drop-shadow(0 6px 20px rgba(0, 30, 45, 0.30))',
          }}
        />
      </Box>

      {/* BADGE FIXO (desktop): rodapé do painel azul com o nome da marca. */}
      <Typography
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          bottom: { md: 32, lg: 40 },
          left: { md: 40, lg: 56, xl: 72 },
          fontFamily: '"Montserrat", system-ui, sans-serif',
          fontWeight: 500,
          fontSize: '0.74rem',
          color: 'rgba(255,255,255,0.55)',
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        Sua Formatura
      </Typography>

      {/* HERO MOBILE (xs/sm): bloco azul empilhado no topo, com logo central.
          No desktop esse bloco some — o azul vem do background fixo. */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'relative',
          width: '100%',
          minHeight: { xs: 120, sm: 150 },
          background: `
            radial-gradient(ellipse at 20% 18%, rgba(9, 175, 223, 0.55), transparent 60%),
            radial-gradient(ellipse at 82% 85%, rgba(0, 74, 97, 0.45), transparent 55%),
            linear-gradient(135deg, #0080ad 0%, #197997 55%, #004A61 100%)
          `,
          alignItems: 'center',
          justifyContent: 'center',
          px: 2.5,
          py: { xs: 1.5, sm: 2 },
          color: '#fff',
        }}
      >
        <Box
          component="img"
          src="/img/logo_branca.png"
          alt={alt || 'Sua Formatura'}
          sx={{
            width: { xs: 130, sm: 160 },
            maxWidth: '55vw',
            height: 'auto',
            filter: 'drop-shadow(0 6px 20px rgba(0, 30, 45, 0.35))',
          }}
        />
      </Box>

      {/* CARD CONTAINER (centralizado horizontalmente).
          Em desktop: atravessa a linha de divisão azul/creme (efeito ponte).
          Em mobile: ocupa quase toda a largura, sobe levemente sobre o hero. */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: { md: '100vh' },
          px: { xs: 1.5, sm: 3, md: 4 },
          py: { xs: 2, sm: 3, md: 6, lg: 8 },
          mt: { xs: -3, sm: -4, md: 0 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 560, md: 720, lg: 820, xl: 880 },
            background: '#fff',
            borderRadius: { xs: '18px', md: '24px' },
            border: '1px solid rgba(0, 128, 173, 0.08)',
            boxShadow: {
              xs: '0 6px 24px rgba(0, 30, 45, 0.10)',
              md: '0 24px 60px rgba(0, 30, 45, 0.18)',
            },
            p: { xs: 2, sm: 3, md: 5, lg: 6 },
            position: 'relative',
            // Garante que o card fique acima do badge "Sua Formatura" fixo
            // mesmo quando o usuário rola até embaixo.
            zIndex: 3,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

SplitView.defaultProps = {
  loading: false,
  children: null,
  contextImage: {},
};

SplitView.propTypes = {
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  contextImage: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string,
  }),
};

export default SplitView;
